import { useEffect, useState } from "react";
import { ListChecks, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTaskSubtasks } from "@/hooks/useTaskSubtasks";
import { getSubtaskDateError } from "@/lib/task-date-validation";
import { SubtaskComposerRow } from "./SubtaskComposerRow";
import { PersistedSubtaskRow } from "./PersistedSubtaskRow";
import type { SubtaskMemberOption } from "./SubtaskConfigFields";

interface TaskSubtaskSectionProps {
  parentTaskId: string;
  workspaceId: string;
  memberId: string;
  isAdmin: boolean;
  /** Dono do pai ou gestor pode criar/editar/concluir subtarefas. */
  canManage: boolean;
  minDate?: string;
  /** Data da tarefa-pai (`YYYY-MM-DD`) para validar limite das subtarefas. */
  parentDueDate?: string | null;
  onOpenSubtask?: (subtaskId: string) => void;
}

export function TaskSubtaskSection({
  parentTaskId,
  workspaceId,
  memberId,
  isAdmin,
  canManage,
  minDate,
  parentDueDate,
  onOpenSubtask,
}: TaskSubtaskSectionProps) {
  const {
    subtasks,
    loading,
    addSubtask,
    updateSubtask,
    toggleAssigneeCompletion,
    removeSubtask,
    reorderSubtasks,
  } = useTaskSubtasks({ parentTaskId, workspaceId, memberId });
  const { toast } = useToast();

  const [members, setMembers] = useState<SubtaskMemberOption[]>([]);
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [justAddedId, setJustAddedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const { data } = await supabase
        .from("workspace_members")
        .select("id, display_name")
        .eq("workspace_id", workspaceId)
        .order("display_name", { ascending: true });
      setMembers((data ?? []) as SubtaskMemberOption[]);
    })();
  }, [isAdmin, workspaceId]);

  const active = subtasks.filter((s) => s.status !== "cancelled");
  const done = active.filter((s) => s.status === "completed").length;

  const reportError = (title: string, err: unknown) =>
    toast({
      title,
      description: err instanceof Error ? err.message : String(err),
      variant: "destructive",
    });

  /** Datas dos vizinhos datados (anterior/próximo) de uma subtarefa na ordem. */
  const siblingDates = (index: number) => ({
    previousSiblingDueDate: [...active.slice(0, index)]
      .reverse()
      .find((s) => s.due_date)?.due_date,
    nextSiblingDueDate: active.slice(index + 1).find((s) => s.due_date)?.due_date,
  });

  /** Erro de data da subtarefa no índice (limite do pai + ordem cronológica). */
  const dateErrorFor = (index: number): string | null =>
    getSubtaskDateError({
      parentDueDate,
      subtaskDueDate: active[index]?.due_date,
      ...siblingDates(index),
    });

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    try {
      setAdding(true);
      const createdId = await addSubtask({
        title: newTitle,
        assignee_member_ids: [memberId],
      });
      setJustAddedId(createdId ?? null);
      setNewTitle("");
    } catch (err: unknown) {
      reportError("Erro ao adicionar subtarefa", err);
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (input: Parameters<typeof updateSubtask>[0]) => {
    if (input.due_date) {
      const index = active.findIndex((s) => s.id === input.id);
      const error = getSubtaskDateError({
        parentDueDate,
        subtaskDueDate: input.due_date,
        ...(index >= 0 ? siblingDates(index) : {}),
      });
      if (error) {
        toast({ title: "Data inválida", description: error, variant: "destructive" });
        return;
      }
    }
    try {
      await updateSubtask(input);
    } catch (err: unknown) {
      reportError("Erro ao atualizar subtarefa", err);
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= active.length) return;
    const ids = active.map((s) => s.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      await reorderSubtasks(ids);
    } catch (err: unknown) {
      reportError("Erro ao reordenar", err);
    }
  };

  const handleToggleAssignee = async (
    taskId: string,
    assigneeMemberId: string,
    completed: boolean,
  ) => {
    try {
      await toggleAssigneeCompletion(taskId, assigneeMemberId, !completed);
    } catch (err: unknown) {
      reportError("Erro ao atualizar subtarefa", err);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await removeSubtask(id);
    } catch (err: unknown) {
      reportError("Erro ao remover subtarefa", err);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium">
          Subtarefas{active.length > 0 ? ` (${done}/${active.length})` : ""}
        </h3>
        {loading && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
      </div>

      {active.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground">Nenhuma subtarefa.</p>
      )}

      {active.length > 0 && (
        <div className="space-y-2">
          {active.map((s, index) => (
            <PersistedSubtaskRow
              key={s.id}
              subtask={s}
              workspaceId={workspaceId}
              memberId={memberId}
              isAdmin={isAdmin}
              canManage={canManage}
              members={members}
              onToggleAssignee={(assigneeMemberId, completed) =>
                void handleToggleAssignee(s.id, assigneeMemberId, completed)
              }
              onUpdate={handleUpdate}
              onRemove={() => void handleRemove(s.id)}
              onMove={(dir) => void move(index, dir)}
              isFirst={index === 0}
              isLast={index === active.length - 1}
              minDate={minDate}
              defaultOpen={s.id === justAddedId}
              dateError={dateErrorFor(index)}
              onOpenDetail={onOpenSubtask}
            />
          ))}
        </div>
      )}

      {canManage && (
        <SubtaskComposerRow
          title={newTitle}
          onTitleChange={setNewTitle}
          onAdd={() => void handleAdd()}
          adding={adding}
        />
      )}
    </div>
  );
}
